import { workoutDraftKey } from '@/lib/fitnessDraft';
import { canonicalFitnessLocalWorkout, fitnessLocalWorkoutId, type FitnessLocalWorkoutEnvelope } from '../../supabase/functions/_shared/fitnessLocalWorkoutDomain';
import { fitnessLocalWorkoutReadRequested } from '../../supabase/functions/_shared/fitnessLocalWorkoutIntent';
import { fitnessDraftDiscardRequested, fitnessDraftDiscardTargeted } from '../../supabase/functions/_shared/fitnessDraftDiscardIntent';

// The existing draft may contain media and resume metadata. Bound that input
// before parsing, then project only the reviewed read-only contract below.
export const FITNESS_LOCAL_WORKOUT_MAX_RAW_LENGTH = 512 * 1024;
type LocalSummary = NonNullable<FitnessLocalWorkoutEnvelope['summary']>;
interface AuthReader {
  getUser(): PromiseLike<{ data: { user: { id: string } | null }; error: unknown }>;
}
export interface FitnessLocalWorkoutReadOptions {
  text: string;
  ownerId: string | null;
  auth: AuthReader;
  isCurrent: () => boolean;
  storage?: () => Pick<Storage, 'getItem'>;
  now?: () => Date;
}
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const bounded = (value: unknown, maximum: number): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= maximum;
const positiveInteger = (value: unknown, maximum: number): value is number => bounded(value, maximum) && Number.isInteger(value) && value > 0;
const invalidDraft = (): never => { throw new Error('invalid_local_workout'); };

function privateDraftSummary(raw: string, owner: string): LocalSummary {
  if (raw.length > FITNESS_LOCAL_WORKOUT_MAX_RAW_LENGTH) return invalidDraft();
  const draft: unknown = JSON.parse(raw);
  if (!object(draft) || draft.version !== 1 || draft.userId !== owner || !Array.isArray(draft.exercises) || draft.exercises.length < 1 || draft.exercises.length > 32 || !object(draft.exerciseConfigs)) return invalidDraft();
  const configs = draft.exerciseConfigs;
  if (typeof draft.startedAt !== 'string' || !Number.isFinite(Date.parse(draft.startedAt))) return invalidDraft();
  if (typeof draft.phase !== 'string' || !['setup', 'countdown', 'execution', 'rest', 'exercise-complete'].includes(draft.phase)) return invalidDraft();
  let totalSets = 0;
  const exercises = draft.exercises.map((exercise: unknown, index: number) => {
    const config = configs[index];
    if (!object(exercise) || typeof exercise.name !== 'string' || typeof exercise.muscle !== 'string' || !bounded(exercise.defaultWeight, 10000) || !positiveInteger(exercise.defaultReps, 100000) || !Array.isArray(exercise.sets) || exercise.sets.length < 1 || exercise.sets.length > 32) return invalidDraft();
    totalSets += exercise.sets.length;
    if (totalSets > 1024 || !object(config) || !bounded(config.weight, 10000) || !positiveInteger(config.reps, 100000) || config.sets !== exercise.sets.length || exercise.timed !== undefined && typeof exercise.timed !== 'boolean') return invalidDraft();
    return { name: exercise.name, timed: exercise.timed === true, sets: exercise.sets.map((set: unknown) => {
      if (!object(set) || !bounded(set.weight, 10000) || !positiveInteger(set.reps, 100000) || typeof set.done !== 'boolean') return invalidDraft();
      return { weight: set.weight, reps: set.reps, done: set.done };
    }) };
  });
  if (!bounded(draft.currentExerciseIndex, exercises.length - 1) || !Number.isInteger(draft.currentExerciseIndex) || !positiveInteger(draft.currentSet, exercises[draft.currentExerciseIndex].sets.length)) return invalidDraft();
  let editingWeight: number | null = null, editingReps: number | null = null, workoutName: string | null = null;
  if (draft.editingWeight != null) {
    if (!bounded(draft.editingWeight, 10000)) return invalidDraft();
    editingWeight = draft.editingWeight;
  }
  if (draft.editingReps != null) {
    if (!bounded(draft.editingReps, 100000) || !Number.isInteger(draft.editingReps)) return invalidDraft();
    // The workout editor starts at zero before any reps have been entered.
    if (draft.editingReps > 0) editingReps = draft.editingReps;
  }
  if (draft.workoutName != null) {
    if (typeof draft.workoutName !== 'string') return invalidDraft();
    if (draft.workoutName !== '') workoutName = draft.workoutName;
  }
  return {
    workoutName,
    savedPhase: draft.phase as LocalSummary['savedPhase'],
    currentExerciseIndex: draft.currentExerciseIndex,
    currentSet: draft.currentSet,
    exercises,
    editingWeight, editingReps,
  };
}

/** Private raw input is projected locally; no resume metadata leaves this API. */
export function projectFitnessLocalWorkoutRaw(raw: string, owner: string, now = new Date()): FitnessLocalWorkoutEnvelope {
  return canonicalFitnessLocalWorkout({ version: 1, owner, observedAt: now.toISOString(), status: 'available', summary: privateDraftSummary(raw, owner) }, owner, now);
}

/** No storage/auth access unless this literal current turn asks for the draft. */
export async function readFitnessLocalWorkoutForRequest(options: FitnessLocalWorkoutReadOptions): Promise<FitnessLocalWorkoutEnvelope | null> {
  if (fitnessDraftDiscardTargeted(options.text) || !fitnessLocalWorkoutReadRequested(options.text) || !options.isCurrent()) return null;
  return readAuthorizedFitnessLocalWorkout(options);
}

/** A discard request authorizes a preview read, never an automatic deletion. */
export async function readFitnessLocalWorkoutForDiscardRequest(options: FitnessLocalWorkoutReadOptions): Promise<FitnessLocalWorkoutEnvelope | null> {
  if (!fitnessDraftDiscardRequested(options.text) || !options.isCurrent()) return null;
  return readAuthorizedFitnessLocalWorkout(options);
}

async function readAuthorizedFitnessLocalWorkout(options: FitnessLocalWorkoutReadOptions): Promise<FitnessLocalWorkoutEnvelope | null> {
  const owner = options.ownerId;
  if (!fitnessLocalWorkoutId(owner)) return null;
  const ownerStillCurrent = async () => {
    if (!options.isCurrent()) return false;
    try {
      const response = await options.auth.getUser();
      return options.isCurrent() && !response.error && response.data.user?.id === owner;
    } catch { return false; }
  };
  if (!await ownerStillCurrent() || !options.isCurrent()) return null;
  let status: FitnessLocalWorkoutEnvelope['status'], summary: LocalSummary | null = null;
  try {
    const storage = options.storage ? options.storage() : window.sessionStorage;
    if (!options.isCurrent()) return null;
    const raw = storage.getItem(workoutDraftKey(owner));
    if (!options.isCurrent()) return null;
    if (raw === null) status = 'absent';
    else {
      try { summary = privateDraftSummary(raw, owner); status = 'available'; }
      catch { status = 'invalid'; }
    }
  } catch { status = 'unavailable'; }
  if (!options.isCurrent() || !await ownerStillCurrent() || !options.isCurrent()) return null;
  const now = options.now?.() ?? new Date();
  const envelope = { version: 1, owner, observedAt: now.toISOString(), status, summary };
  try { return canonicalFitnessLocalWorkout(envelope, owner, now); }
  catch {
    // A malformed projected field remains distinguishable from no draft.
    return canonicalFitnessLocalWorkout({ ...envelope, status: 'invalid', summary: null }, owner, now);
  }
}
